import { useDndContext, useDraggable, useDroppable } from '@dnd-kit/core';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import AttributeComponent from 'components/common/attribute';
import BTUBubbles from 'components/common/btu-bubbles';
import { useContextMenu } from 'components/common/context-menu-manager';
import GridSpaceOutlineSvg from 'components/common/grid-space-outline-svg';
import OpenFloorPlanWalls from 'components/common/open-floor-plan-walls';
import ConditioningToggle from 'components/structure-page/2d/conditioning-toggle';
import SkylightBadge from 'components/structure-page/2d/skylight-badge';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  ContextMenuItemType,
  EdgeDirectionType,
  EdgeItemsType,
  EdgeItemType,
  ExposureDirectionType,
  FootprintPolygonsType,
  GridSpaceDataType,
  PartialWithRequired,
  SpaceType
} from 'data/types';
import {
  updateStructureItem,
  updateStructureItemAttribute
} from 'features/structure';
import { removeSkylightsFromSpace } from 'features/structure/structure-thunks';
import { setActiveItem } from 'features/ui';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { getSignificantAppliances } from 'utils/appliances';
import { EMPTY_FOOTPRINT_POLYGONS } from 'utils/footprint-polygons';
import {
  getAdjacentSpaces as getAdjacentSpacesFromGrid,
  getAllAdjacentSpaces,
  getOpenToBorderRadiusClass,
  isSmallSpace
} from 'utils/grid';
import {
  getGridSpaceOutlineStroke,
  getGridSpaceOutlineStrokeWidth
} from 'utils/grid-space-outline-style';
import {
  leftoverRebuildTargetsForLevel,
  leftoverUniqueIdsForLevel
} from 'utils/leftover-space-sync';
import {
  openingRunsWithOpenNeighborOverhang,
  openingRunWithOpenNeighborOverhang
} from 'utils/open-neighbor-opening-runs';
import {
  getGridSpaceOutlineVertices,
  shouldUseOpenToOutline
} from 'utils/open-to-outline';
import {
  computeOpeningDragPlacement,
  isOpeningDragType,
  OPENING_PRESETS,
  openingWidthForDragType,
  sidebarOpeningDropPreview
} from 'utils/opening-drag';
import {
  getOpeningDragPointer,
  setLiveOpeningDragPlacement,
  setOpeningDragPointer
} from 'utils/opening-drag-live-placement';
import { getSpaceEdgeRuns } from 'utils/opening-layout';
import {
  EdgeRunType,
  OpeningPlacementType
} from 'utils/opening-position-types';
import { getPolygonEdgeOpeningRuns } from 'utils/polygon-edge-opening-runs';
import { getHiddenIntervalsPerEdgeForGridSpace } from 'utils/polygon-shared-edges';
import { resolveSpaceLabelLayout } from 'utils/space-label-layout';
import type { SpaceLabelObstacle } from 'utils/space-label-position';
import { spaceRectVertices } from 'utils/space-label-position';
import { formatSpaceLabelDimensions } from 'utils/space-label-size';
import {
  spaceOpenNeighborRuns,
  spaceOriginFeet
} from 'utils/space-opening-run';
import {
  isPolygonGridSpace,
  polygonArea,
  verticesToCssPolygon
} from 'utils/space-polygon';
import { spaceHasWallOnEdge } from 'utils/space-wall-on-edge';
import {
  deleteEdgeItem,
  findLevelBySpaceId,
  findSpaceById,
  getEdgeItemsFromSpace,
  isDetachedSpaceItem,
  shouldShowSpaceLabel,
  updateWindowSize
} from 'utils/structure';
import { createActiveItem, errorModal } from 'utils/ui';
import { createGridSpaceDropData } from './grid-space-drop-data';
import {
  GRID_SPACE_ELEVATED_Z_CLASS,
  GRID_SPACE_POLYGON_HANDLES_SELECTED_Z_CLASS,
  GRID_SPACE_POLYGON_HANDLES_Z_CLASS,
  getGridSpaceOpeningsOverlayZClass
} from './grid-space-overlay-stacking';
import OpeningDropPreview from './opening-drop-preview';
import PolygonHandles from './polygon-handles';
import SpaceEdgeOpenings from './space-edge-openings';
import SpaceInfoHoverCard from './space-info-hover-card';
import SpaceInfoPopover from './space-info-popover';
import { useGridSpaceContextMenu } from './use-grid-space-context-menu';
import { useGridSpaceResize } from './use-grid-space-resize';
import { useGridSpaceVertexEditing } from './use-grid-space-vertex-editing';
import { usePolygonHandleDrag } from './use-polygon-handle-drag';

export type { GridSpaceDropData } from './grid-space-drop-data';

type GridSpaceProps = {
  gridSpace: GridSpaceDataType;
  id?: string;
  allGridSpaces?: GridSpaceDataType[];
  isBeingDragged?: boolean;
  overlay?: boolean;
  mode?:
    | 'spaces'
    | 'conditioning'
    | 'ceilings'
    | 'fenestration'
    | 'electrical'
    | 'summary'
    | 'static'
    | 'zones'
    | 'appliances';
  isSelected?: boolean;
  isPendingSelection?: boolean;
  selectedSpaceIds?: Set<string>;
  onSelect?: (spaceId: string, isMultiSelect: boolean) => void;
  opacity?: number;
  showSectionHighlighting?: boolean;
  onZoneSpaceClick?: (spaceId: string, sectionId: string) => void;
  zoom?: number;
  enableContextMenuInStaticMode?: boolean;
  contextMenuFilter?: (items: ContextMenuItemType[]) => ContextMenuItemType[];
  suppressInternalLabel?: boolean;
  onSectionHover?: (sectionId: string, isHovered: boolean) => void;
  labelObstacles?: SpaceLabelObstacle[];
  footprintPolygons?: FootprintPolygonsType | null;
  showSpaceBtus?: boolean;
};

function GridSpace({
  gridSpace,
  id,
  allGridSpaces = [],
  isBeingDragged = false,
  overlay = false,
  mode = 'summary',
  isSelected = false,
  isPendingSelection = false,
  selectedSpaceIds = new Set(),
  onSelect,
  opacity = 100,
  showSectionHighlighting = false,
  onZoneSpaceClick,
  zoom = 1,
  enableContextMenuInStaticMode = false,
  contextMenuFilter,
  suppressInternalLabel = false,
  onSectionHover,
  labelObstacles = [],
  footprintPolygons = null,
  showSpaceBtus = false
}: GridSpaceProps) {
  const dispatch = useAppDispatch();
  const structure = useAppSelector((state) => state.structure.structure);
  const autoLayoutSpaceIds = useAppSelector(
    (state) => state.structure.autoLayoutSpaceIds
  );
  const {
    items,
    applianceItems,
    attributes: catalogAttributes
  } = useAppSelector((state) => state.data);
  const { easyMode, showZones } = useAppSelector((state) => state.ui);
  const { projectSettings } = useAppSelector((state) => state.project);
  const [justDropped, setJustDropped] = useState(false);
  const [hoveredEdge, setHoveredEdge] = useState<EdgeDirectionType | null>(
    null
  );
  const [activeEdge, setActiveEdge] = useState<EdgeDirectionType | null>(null);
  const [contextMenuEdgeIndex, setContextMenuEdgeIndex] = useState<
    number | null
  >(null);
  const [activeSpace, setActiveSpace] = useState(false);
  const [isSpaceHovered, setIsSpaceHovered] = useState(false);
  const [openingDragPlacement, setOpeningDragPlacement] =
    useState<OpeningPlacementType | null>(null);
  const { closeContextMenu, contextMenu, openContextMenu } = useContextMenu();
  const containerRef = useRef<HTMLDivElement>(null);
  const space = useMemo(
    () => findSpaceById(structure, gridSpace.spaceId),
    [structure, gridSpace.spaceId]
  );
  const spaceLevel = useMemo(
    () => findLevelBySpaceId(structure, gridSpace.spaceId),
    [structure, gridSpace.spaceId]
  );
  const leftoverIds = useMemo(
    () => leftoverUniqueIdsForLevel(spaceLevel, autoLayoutSpaceIds),
    [spaceLevel, autoLayoutSpaceIds]
  );
  const rebuildLeftoverOnResize =
    leftoverRebuildTargetsForLevel(spaceLevel, autoLayoutSpaceIds, [
      gridSpace.spaceId
    ]) !== null;
  const significantAppliances = useMemo(
    () => getSignificantAppliances(applianceItems),
    [applianceItems]
  );
  const ceilingType = space?.attributes.find(
    (att) => att.code === 'ceiling_type'
  );
  const ceilingHeight = space?.attributes.find(
    (att) => att.code === 'ceiling_height'
  );
  const slopedHeight = space?.attributes.find(
    (att) => att.code === 'sloped_height'
  );
  const vaultedHeight = space?.attributes.find(
    (att) => att.code === 'vaulted_height'
  );

  const isStatic = mode === 'static';
  const isZonesMode = mode === 'zones';
  const isStaticOrZones = isStatic || isZonesMode;

  const effectiveFootprints = useMemo(
    () =>
      gridSpace.itemId != null && isDetachedSpaceItem(gridSpace.itemId)
        ? EMPTY_FOOTPRINT_POLYGONS
        : footprintPolygons ?? EMPTY_FOOTPRINT_POLYGONS,
    [gridSpace.itemId, footprintPolygons]
  );

  const isPolygonSpace =
    !easyMode &&
    isPolygonGridSpace(
      gridSpace.vertices,
      gridSpace.position.width,
      gridSpace.position.height
    );

  const homeOrientation = useMemo(() => {
    const orientationSetting = projectSettings.find(
      (s) => s.code === 'structure_orientation'
    );
    return orientationSetting?.value as ExposureDirectionType | undefined;
  }, [projectSettings]);

  const hasWallOnEdge = useCallback(
    (edge: 'top' | 'right' | 'bottom' | 'left'): boolean =>
      spaceHasWallOnEdge(space?.sizingAttributes, edge, homeOrientation),
    [space?.sizingAttributes, homeOrientation]
  );

  const originFeet = useMemo(
    () =>
      space
        ? spaceOriginFeet(space)
        : {
            x: gridSpace.position.x / STRUCTURE_GRID_SIZE,
            y: gridSpace.position.y / STRUCTURE_GRID_SIZE
          },
    [space, gridSpace.position.x, gridSpace.position.y]
  );

  const openNeighborRuns = useMemo(
    () =>
      space ? spaceOpenNeighborRuns(space, spaceLevel, homeOrientation) : [],
    [space, spaceLevel, homeOrientation]
  );

  const polygonOpeningLayouts = useMemo(() => {
    if (!isPolygonSpace || !space?.sizingAttributes || !gridSpace.vertices) {
      return null;
    }
    return getPolygonEdgeOpeningRuns({
      originFeet,
      verticesFeet: gridSpace.vertices.map((vertex) => ({
        x: vertex.x / STRUCTURE_GRID_SIZE,
        y: vertex.y / STRUCTURE_GRID_SIZE
      })),
      footprints: effectiveFootprints,
      walls: [
        ...space.sizingAttributes.aboveGradeWalls,
        ...space.sizingAttributes.belowGradeWalls
      ],
      homeOrientation
    }).map((layout) => ({
      ...layout,
      run: openingRunWithOpenNeighborOverhang(
        layout.run,
        originFeet,
        openNeighborRuns
      )
    }));
  }, [
    isPolygonSpace,
    space?.sizingAttributes,
    gridSpace.vertices,
    originFeet,
    effectiveFootprints,
    homeOrientation,
    openNeighborRuns
  ]);

  const edgeRuns = useMemo(() => {
    if (polygonOpeningLayouts && polygonOpeningLayouts.length > 0) {
      return polygonOpeningLayouts.map((layout) => layout.run);
    }
    return openingRunsWithOpenNeighborOverhang(
      getSpaceEdgeRuns(gridSpace, effectiveFootprints),
      originFeet,
      openNeighborRuns
    );
  }, [
    polygonOpeningLayouts,
    gridSpace,
    effectiveFootprints,
    originFeet,
    openNeighborRuns
  ]);
  const spaceWidthFt = gridSpace.position.width / STRUCTURE_GRID_SIZE;
  const spaceHeightFt = gridSpace.position.height / STRUCTURE_GRID_SIZE;
  const effectiveZoom = zoom > 0 ? zoom : 1;
  const { isResizing, handleResizeStart, handleEdgeResizeStart } =
    useGridSpaceResize({
      gridSpace,
      allGridSpaces,
      effectiveFootprints,
      effectiveZoom,
      isSelected,
      leftoverIds,
      rebuildLeftoverOnResize,
      spaceLevel,
      structure,
      autoLayoutSpaceIds,
      catalogAttributes
    });

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: gridSpace.id,
      disabled: isResizing || overlay || isStaticOrZones,
      data: {
        type: 'grid-space',
        space: gridSpace,
        sectionId: gridSpace.sectionId,
        spacesInSection: gridSpace.spacesInSection
      }
    });

  const { active } = useDndContext();
  const activeDragType = active?.data.current?.type as string | undefined;
  const activeDragItemId = active?.data.current?.itemId as string | undefined;
  const isHomeOfDraggedOpening =
    (activeDragType === 'grid-window' || activeDragType === 'grid-door') &&
    active?.data.current?.spaceId === gridSpace.spaceId;
  const dropEdge = openingDragPlacement?.edge ?? null;
  const dropPreview = sidebarOpeningDropPreview(activeDragType);

  const canAcceptDrop = false;

  const canAcceptWindowOrDoor =
    !overlay &&
    !isStaticOrZones &&
    !isDragging &&
    !isBeingDragged &&
    active?.data.current?.type &&
    (isOpeningDragType(active.data.current.type) ||
      ['grid-window', 'grid-door'].includes(active.data.current.type));

  const canAcceptAppliance =
    !overlay &&
    !isDragging &&
    !isBeingDragged &&
    active?.data.current?.type === 'new-appliance';

  const canAcceptSkylight =
    !overlay &&
    !isDragging &&
    !isBeingDragged &&
    (active?.data.current?.type === 'new-skylight-small' ||
      active?.data.current?.type === 'new-skylight-medium' ||
      active?.data.current?.type === 'new-skylight-large') &&
    (space?.sizingAttributes?.ceilingsBelowRoof?.length ?? 0) > 0;

  const { setNodeRef: setDroppableRef, isOver: isDroppableOver } = useDroppable(
    {
      id: `drop-${gridSpace.id}`,
      disabled:
        isStaticOrZones ||
        (!canAcceptDrop &&
          !canAcceptWindowOrDoor &&
          !canAcceptAppliance &&
          !canAcceptSkylight),
      data: createGridSpaceDropData(
        gridSpace.id,
        gridSpace.sectionId,
        openingDragPlacement
      )
    }
  );

  const wasBeingDraggedRef = useRef(false);

  useEffect(() => {
    if (wasBeingDraggedRef.current && !isBeingDragged && !isDragging) {
      setJustDropped(true);
      const timer = setTimeout(() => setJustDropped(false), 50);
      wasBeingDraggedRef.current = isBeingDragged;
      return () => clearTimeout(timer);
    }
    wasBeingDraggedRef.current = isBeingDragged;
    return undefined;
  }, [isBeingDragged, isDragging]);

  useEffect(() => {
    if (isDragging) {
      closeContextMenu();
    }
  }, [isDragging, closeContextMenu]);

  useEffect(() => {
    const clearOpeningPlacement = () => {
      setOpeningDragPlacement(null);
      setLiveOpeningDragPlacement(gridSpace.id, null);
    };

    if (!canAcceptWindowOrDoor) {
      clearOpeningPlacement();
      return undefined;
    }

    const applyPlacement = (clientX: number, clientY: number) => {
      if (!containerRef.current) {
        clearOpeningPlacement();
        return;
      }

      const availableRuns = edgeRuns.filter((run) => hasWallOnEdge(run.edge));
      const widthFt = openingWidthForDragType(
        active?.data.current?.type,
        active?.data.current?.item
      );
      const placement = computeOpeningDragPlacement({
        clientX,
        clientY,
        spaceRect: containerRef.current.getBoundingClientRect(),
        runs: availableRuns,
        widthFt,
        spaceWidthFt,
        spaceHeightFt
      });
      setOpeningDragPlacement(placement);
      setLiveOpeningDragPlacement(gridSpace.id, placement);
    };

    const handlePointerMove = (event: PointerEvent) => {
      setOpeningDragPointer(event.clientX, event.clientY);
      if (!isDroppableOver && !isHomeOfDraggedOpening) {
        clearOpeningPlacement();
        return;
      }
      applyPlacement(event.clientX, event.clientY);
    };

    window.addEventListener('pointermove', handlePointerMove);

    if (isDroppableOver || isHomeOfDraggedOpening) {
      const pointer = getOpeningDragPointer();
      if (pointer) {
        applyPlacement(pointer.x, pointer.y);
      }
    } else {
      clearOpeningPlacement();
    }

    return () => window.removeEventListener('pointermove', handlePointerMove);
  }, [
    active,
    canAcceptWindowOrDoor,
    edgeRuns,
    hasWallOnEdge,
    isDroppableOver,
    isHomeOfDraggedOpening,
    spaceHeightFt,
    spaceWidthFt,
    gridSpace.id
  ]);

  const shouldShowLabel = useCallback(() => {
    return shouldShowSpaceLabel(gridSpace, allGridSpaces, structure);
  }, [gridSpace, allGridSpaces, structure]);

  const getAdjacentSpaces = useCallback(() => {
    return getAdjacentSpacesFromGrid(gridSpace, allGridSpaces);
  }, [gridSpace, allGridSpaces]);

  const getEdgeItems = useCallback((): EdgeItemsType => {
    return getEdgeItemsFromSpace(structure, gridSpace.spaceId, homeOrientation);
  }, [structure, gridSpace.spaceId, homeOrientation]);

  const { getContextMenuItems, handleContextMenu } = useGridSpaceContextMenu({
    gridSpace,
    allGridSpaces,
    structure,
    selectedSpaceIds,
    mode,
    significantAppliances,
    items,
    hasWallOnEdge,
    contextMenuFilter,
    setActiveEdge,
    setActiveSpace
  });

  const { handleVertexContextMenu, handleEdgeContextMenu, handleEdgeHover } =
    useGridSpaceVertexEditing({
      gridSpace,
      allGridSpaces,
      effectiveFootprints,
      edgeRuns,
      getContextMenuItems,
      contextMenuFilter,
      setActiveEdge,
      setActiveSpace,
      setContextMenuEdgeIndex,
      setHoveredEdge
    });

  const handleSpaceClick = useCallback(
    (e: React.MouseEvent) => {
      if (overlay) return;
      if (isZonesMode && onZoneSpaceClick && gridSpace.sectionId) {
        onZoneSpaceClick(gridSpace.spaceId, gridSpace.sectionId);
        return;
      }
      if (isStatic) return;
      if (e.detail === 1 && onSelect) {
        const isMultiSelect = e.ctrlKey || e.metaKey || e.shiftKey;
        onSelect(gridSpace.spaceId, isMultiSelect);
      }
    },
    [
      overlay,
      isStatic,
      isZonesMode,
      onSelect,
      onZoneSpaceClick,
      gridSpace.spaceId,
      gridSpace.sectionId
    ]
  );

  const handleSpaceDoubleClick = useCallback(() => {
    if (overlay || isStatic) return;
    const structureSpace = findSpaceById(structure, gridSpace.spaceId);
    if (structureSpace) {
      dispatch(
        setActiveItem(
          createActiveItem({
            type: 'space',
            item: structureSpace
          })
        )
      );
    }
  }, [overlay, isStatic, structure, gridSpace.spaceId, dispatch]);

  const handleSpaceKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (overlay || isStatic) return;
      if ((e.key === 'Enter' || e.key === ' ') && onSelect) {
        e.preventDefault();
        const isMultiSelect = e.ctrlKey || e.metaKey || e.shiftKey;
        onSelect(gridSpace.spaceId, isMultiSelect);
      }
    },
    [overlay, isStatic, onSelect, gridSpace.spaceId]
  );

  useEffect(() => {
    if (!contextMenu.visible) {
      setActiveEdge(null);
      setActiveSpace(false);
      setContextMenuEdgeIndex(null);
    } else {
      const isThisSpaceMenu = contextMenu.sourceId?.startsWith(
        `${gridSpace.id}-`
      );

      if (!isThisSpaceMenu) {
        setActiveEdge(null);
        setActiveSpace(false);
        setContextMenuEdgeIndex(null);
      }
    }
  }, [contextMenu.visible, contextMenu.sourceId, gridSpace.id]);

  const inMultiSpaceSection =
    !!gridSpace.sectionId && (gridSpace.spacesInSection ?? 1) > 1;

  const polygonHandleDrag = usePolygonHandleDrag({
    gridSpace,
    allGridSpaces,
    footprints: effectiveFootprints,
    effectiveZoom,
    beginCornerResize: handleResizeStart,
    beginEdgeResize: handleEdgeResizeStart
  });

  const handleChangeAttribute = (attributeId: number, value: any) => {
    if (space) {
      dispatch(
        updateStructureItemAttribute({
          uniqueId: space.uniqueId,
          attributeId,
          value
        })
      );
    } else {
      console.error('Space not found');
      errorModal('Space not found');
    }
  };

  const handleConditionedChange = (value: boolean) => {
    if (!space) return;
    dispatch(
      updateStructureItem({
        uniqueId: space.uniqueId,
        conditioned: value
      } as PartialWithRequired<SpaceType, 'uniqueId'>)
    );
  };

  const useOpenToOutline = shouldUseOpenToOutline(
    isPolygonSpace,
    space?.openTo,
    easyMode
  );
  const outlineVertices = useMemo(
    () => getGridSpaceOutlineVertices(gridSpace),
    [gridSpace]
  );
  const isSpaceRootElevated =
    (!isStaticOrZones && isSpaceHovered) ||
    (!useOpenToOutline && (activeSpace || isSelected || isPendingSelection));

  const getContainerClasses = useCallback(() => {
    const adjacentSpaces = getAdjacentSpaces();

    const borderRadiusClass =
      adjacentSpaces.top ||
      adjacentSpaces.right ||
      adjacentSpaces.bottom ||
      adjacentSpaces.left
        ? getOpenToBorderRadiusClass(gridSpace, allGridSpaces, structure)
        : 'rounded';

    const hasSectionColor =
      (showSectionHighlighting || showZones) &&
      gridSpace.sectionColor &&
      !overlay;

    return clsx(
      'absolute group z-[10] select-none',
      !overlay && isSpaceRootElevated && GRID_SPACE_ELEVATED_Z_CLASS,
      overlay && 'bg-transparent',
      !overlay &&
        !hasSectionColor &&
        (space?.conditioned ? 'bg-white' : 'bg-gray-200'),
      overlay && 'border-gray-300 border-dashed',
      !useOpenToOutline &&
        !overlay &&
        !hasSectionColor &&
        !(activeSpace || isSelected || isPendingSelection) &&
        'border-gray-400',
      !useOpenToOutline &&
        !overlay &&
        (activeSpace || isSelected || isPendingSelection) &&
        'border-blue-500',
      !useOpenToOutline && borderRadiusClass,
      !useOpenToOutline &&
        !overlay &&
        (activeSpace || isSelected || isPendingSelection) &&
        'border-4',
      !useOpenToOutline &&
        !overlay &&
        !activeSpace &&
        !isSelected &&
        !isPendingSelection &&
        'border-2',
      !useOpenToOutline && overlay && 'border-2',
      isBeingDragged && 'opacity-0 pointer-events-none',
      !overlay &&
        !isStaticOrZones &&
        !isBeingDragged &&
        isDragging &&
        'z-[1000] opacity-80 cursor-grabbing',
      !overlay &&
        !isStaticOrZones &&
        !isBeingDragged &&
        !isDragging &&
        'opacity-100 cursor-grab',
      isStatic && !isZonesMode && 'cursor-default',
      isZonesMode && 'cursor-pointer',
      !overlay &&
        !isStaticOrZones &&
        isDroppableOver &&
        canAcceptDrop &&
        'ring-4 ring-green-400 ring-opacity-50',
      !overlay &&
        !isStaticOrZones &&
        isDroppableOver &&
        canAcceptAppliance &&
        'ring-4 ring-purple-400 ring-opacity-50',
      !overlay &&
        !isStaticOrZones &&
        isDroppableOver &&
        canAcceptSkylight &&
        'ring-4 ring-yellow-400 ring-opacity-50',
      overlay && 'pointer-events-none z-[0]'
    );
  }, [
    getAdjacentSpaces,
    activeSpace,
    isDragging,
    isBeingDragged,
    isDroppableOver,
    canAcceptDrop,
    canAcceptAppliance,
    canAcceptSkylight,
    allGridSpaces,
    gridSpace,
    overlay,
    showZones,
    showSectionHighlighting,
    structure,
    space,
    isStatic,
    isStaticOrZones,
    isSelected,
    isPendingSelection,
    isPolygonSpace,
    isSpaceRootElevated,
    useOpenToOutline
  ]);

  const hasSectionColor =
    (showSectionHighlighting || showZones) &&
    gridSpace.sectionColor &&
    !overlay;
  const textColorClass = overlay ? 'text-gray-400' : 'text-gray-700';

  const classes = clsx(
    'flex h-full w-full flex-col items-center p-2 text-center text-sm font-medium select-none',
    'justify-center',
    textColorClass,
    !overlay && !isStaticOrZones && !isDragging && !isResizing && 'cursor-grab',
    !overlay && !isStaticOrZones && isDragging && 'cursor-grabbing',
    isStatic && !isZonesMode && 'cursor-default',
    isZonesMode && 'cursor-pointer'
  );

  const getEffectiveOpacity = () => (isBeingDragged ? 0 : opacity / 100);

  const polygonClipPath = isPolygonSpace
    ? verticesToCssPolygon(gridSpace.vertices!)
    : undefined;

  const spaceBoxWidth = easyMode
    ? STRUCTURE_GRID_SIZE * 10
    : gridSpace.position.width;
  const spaceBoxHeight = easyMode
    ? STRUCTURE_GRID_SIZE * 10
    : gridSpace.position.height;
  const showSpaceName =
    shouldShowLabel() &&
    !overlay &&
    mode !== 'ceilings' &&
    !suppressInternalLabel;
  const isSmallNamedSpace = isSmallSpace(
    gridSpace.position.width,
    gridSpace.position.height
  );
  const needsClickHandling = isStaticOrZones && enableContextMenuInStaticMode;
  const showConditioningToggle =
    mode === 'conditioning' && !!space && showSpaceName;
  const showSpaceNameDimensions =
    !easyMode &&
    !isStatic &&
    !showSpaceBtus &&
    mode !== 'zones' &&
    mode !== 'conditioning';
  const showSpaceNameBtus =
    (isStatic || showSpaceBtus) &&
    !!space &&
    space.conditioned !== false &&
    !showSectionHighlighting;
  const spaceLabelVertices =
    isPolygonSpace && gridSpace.vertices
      ? gridSpace.vertices
      : spaceRectVertices(spaceBoxWidth, spaceBoxHeight);
  const polygonSqft =
    gridSpace.vertices &&
    isPolygonGridSpace(
      gridSpace.vertices,
      gridSpace.position.width,
      gridSpace.position.height
    )
      ? Math.round(polygonArea(gridSpace.vertices) / 100)
      : null;
  const spaceLabelDimensionsText = showSpaceNameDimensions
    ? formatSpaceLabelDimensions({
        widthPx: gridSpace.position.width,
        heightPx: gridSpace.position.height,
        squareFootage: polygonSqft
      })
    : undefined;
  const spaceLabelFit = resolveSpaceLabelLayout({
    label: gridSpace.label,
    spaceWidth: spaceBoxWidth,
    spaceHeight: spaceBoxHeight,
    zoom: effectiveZoom,
    showBtus: showSpaceNameBtus,
    showDimensions: showSpaceNameDimensions,
    dimensionsText: spaceLabelDimensionsText,
    showToggle: showConditioningToggle,
    allowWrap: !isSmallNamedSpace && mode !== 'appliances',
    vertices: spaceLabelVertices,
    obstacles: labelObstacles
  });
  const spaceLabelAnchor = spaceLabelFit.anchor;
  const needsOverflowHover =
    spaceLabelFit.isAbbreviated ||
    spaceLabelFit.showBtusInHover ||
    spaceLabelFit.showDimensionsInHover;
  const useSpaceNamePopover =
    showSpaceName &&
    (mode === 'appliances' ||
      (isSmallNamedSpace &&
        needsOverflowHover &&
        labelObstacles.length === 0)) &&
    !needsClickHandling;
  const showInlineSpaceLabel = showSpaceName && !useSpaceNamePopover;
  const showOverlayConditioningToggle =
    showConditioningToggle && showInlineSpaceLabel && !!spaceLabelAnchor;
  const showBodyConditioningToggle =
    showConditioningToggle && !showOverlayConditioningToggle;
  const elevateSpaceLabel =
    isSpaceHovered ||
    activeSpace ||
    isSelected ||
    isPendingSelection ||
    isDragging;
  const openingsInteractive = !isStaticOrZones;

  const containerStyle: React.CSSProperties = {
    left: gridSpace.position.x,
    top: gridSpace.position.y,
    width: spaceBoxWidth,
    height: spaceBoxHeight,
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition: isDragging || justDropped ? 'none' : undefined,
    opacity: getEffectiveOpacity(),
    ...(hasSectionColor && gridSpace.sectionColor
      ? {
          backgroundColor: gridSpace.sectionColor.bg,
          borderColor: gridSpace.sectionColor.border
        }
      : {}),
    ...(polygonClipPath
      ? {
          clipPath: polygonClipPath,
          WebkitClipPath: polygonClipPath
        }
      : {})
  };

  const refCallback = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      setDroppableRef(node);
      (containerRef as React.MutableRefObject<HTMLDivElement | null>).current =
        node;
    },
    [setNodeRef, setDroppableRef]
  );

  const polygonHandlesOverlayStyle: React.CSSProperties = {
    left: gridSpace.position.x,
    top: gridSpace.position.y,
    width: spaceBoxWidth,
    height: spaceBoxHeight,
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition: isDragging || justDropped ? 'none' : undefined,
    opacity: getEffectiveOpacity()
  };

  return (
    <>
      <div
        ref={refCallback}
        className={getContainerClasses()}
        style={containerStyle}
        data-grid-space
        role='button'
        tabIndex={overlay || (isStatic && !isZonesMode) ? -1 : 0}
        onClick={handleSpaceClick}
        onDoubleClick={handleSpaceDoubleClick}
        onKeyDown={handleSpaceKeyDown}
        onMouseEnter={() => {
          setIsSpaceHovered(true);
          if (gridSpace.sectionId && onSectionHover) {
            onSectionHover(gridSpace.sectionId, true);
          }
        }}
        onMouseLeave={() => {
          setIsSpaceHovered(false);
          if (gridSpace.sectionId && onSectionHover) {
            onSectionHover(gridSpace.sectionId, false);
          }
        }}
      >
        <div
          className={classes}
          style={{
            pointerEvents:
              overlay ||
              isResizing ||
              (isStaticOrZones && !enableContextMenuInStaticMode)
                ? 'none'
                : 'auto',
            ...(hasSectionColor && gridSpace.sectionColor && !overlay
              ? { color: gridSpace.sectionColor.text }
              : {})
          }}
          onContextMenu={
            overlay || (isStaticOrZones && !enableContextMenuInStaticMode)
              ? undefined
              : (e) => handleContextMenu(e, 'space')
          }
          onClick={
            isZonesMode && enableContextMenuInStaticMode
              ? (e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleSpaceClick(e);
                }
              : undefined
          }
          {...(overlay || isStaticOrZones ? {} : attributes)}
          {...(overlay || isStaticOrZones ? {} : listeners)}
          id={id}
        >
          {showSpaceName && (
            <>
              {useSpaceNamePopover && (
                <SpaceInfoPopover
                  label={gridSpace.label}
                  displayLabel={spaceLabelFit.displayLabel}
                  zoom={effectiveZoom}
                  widthFt={gridSpace.position.width / 10}
                  heightFt={gridSpace.position.height / 10}
                  squareFootage={polygonSqft}
                  BTUs={space?.BTUs}
                  showBTUs={
                    (isSmallNamedSpace || spaceLabelFit.showBtusInHover) &&
                    showSpaceNameBtus
                  }
                  hasSectionColor={hasSectionColor}
                  sectionTextColor={gridSpace.sectionColor?.text}
                  applianceCount={
                    space?.sizingAttributes?.appliances?.length ?? 0
                  }
                  showApplianceCount={mode === 'appliances'}
                  appliances={space?.sizingAttributes?.appliances}
                  onRemoveAppliance={
                    mode === 'appliances' && space?.sizingAttributes
                      ? (applianceUniqueId) => {
                          const sa = space.sizingAttributes!;
                          dispatch(
                            updateStructureItem({
                              uniqueId: gridSpace.spaceId,
                              sizingAttributes: {
                                ...sa,
                                appliances:
                                  sa.appliances?.filter(
                                    (a) => a.uniqueId !== applianceUniqueId
                                  ) ?? []
                              }
                            })
                          );
                        }
                      : undefined
                  }
                  isSmallSpace={isSmallNamedSpace}
                  showTriggerDimensions={spaceLabelFit.showDimensionsOnSpace}
                  showHoverDimensions={spaceLabelFit.showDimensionsInHover}
                  dimensionsText={spaceLabelDimensionsText}
                />
              )}
              {showBodyConditioningToggle && space && (
                <ConditioningToggle
                  spaceId={gridSpace.spaceId}
                  value={space.conditioned !== false}
                  onChange={handleConditionedChange}
                />
              )}
            </>
          )}
          {shouldShowLabel() &&
            !overlay &&
            mode === 'ceilings' &&
            !suppressInternalLabel &&
            space &&
            ceilingType &&
            ceilingHeight &&
            slopedHeight &&
            vaultedHeight && (
              <>
                <AttributeComponent
                  {...ceilingType}
                  onChange={(value) =>
                    handleChangeAttribute(ceilingType.id, value)
                  }
                  required
                  completed={!!ceilingType.value}
                />
                <AttributeComponent
                  {...ceilingHeight}
                  onChange={(value) =>
                    handleChangeAttribute(ceilingHeight.id, value)
                  }
                  required
                  completed={!!ceilingHeight.value}
                />
                {ceilingType.value === 'Sloped' && (
                  <AttributeComponent
                    {...slopedHeight}
                    onChange={(value) =>
                      handleChangeAttribute(slopedHeight.id, value)
                    }
                    required
                    completed={!!slopedHeight.value}
                  />
                )}
                {ceilingType.value === 'Vaulted' && (
                  <AttributeComponent
                    {...vaultedHeight}
                    onChange={(value) =>
                      handleChangeAttribute(vaultedHeight.id, value)
                    }
                    required
                    completed={!!vaultedHeight.value}
                  />
                )}
              </>
            )}
        </div>
        {!overlay && !isStaticOrZones && (inMultiSpaceSection || easyMode) && (
          <>
            <div
              aria-hidden='true'
              className={clsx(
                'absolute -top-1 left-2 right-2 h-2 cursor-n-resize transition-colors',
                activeEdge === 'top' && 'bg-blue-500',
                dropEdge === 'top' && 'bg-green-400 bg-opacity-70',
                hoveredEdge === 'top' &&
                  activeEdge !== 'top' &&
                  dropEdge !== 'top' &&
                  'bg-blue-300 bg-opacity-50'
              )}
              onMouseEnter={() => handleEdgeHover('top')}
              onMouseLeave={() => handleEdgeHover(null)}
              onContextMenu={(e) => handleContextMenu(e, 'top')}
              onMouseDown={(e) => handleEdgeResizeStart(e, 'top')}
            />
            <div
              aria-hidden='true'
              className={clsx(
                'absolute -right-1 bottom-2 top-2 w-2 cursor-e-resize transition-colors',
                activeEdge === 'right' && 'bg-blue-500',
                dropEdge === 'right' && 'bg-green-400 bg-opacity-70',
                hoveredEdge === 'right' &&
                  activeEdge !== 'right' &&
                  dropEdge !== 'right' &&
                  'bg-blue-300 bg-opacity-50'
              )}
              onMouseEnter={() => handleEdgeHover('right')}
              onMouseLeave={() => handleEdgeHover(null)}
              onContextMenu={(e) => handleContextMenu(e, 'right')}
              onMouseDown={(e) => handleEdgeResizeStart(e, 'right')}
            />
            <div
              aria-hidden='true'
              className={clsx(
                'absolute -bottom-1 left-2 right-2 h-2 cursor-s-resize transition-colors',
                activeEdge === 'bottom' && 'bg-blue-500',
                dropEdge === 'bottom' && 'bg-green-400 bg-opacity-70',
                hoveredEdge === 'bottom' &&
                  activeEdge !== 'bottom' &&
                  dropEdge !== 'bottom' &&
                  'bg-blue-300 bg-opacity-50'
              )}
              onMouseEnter={() => handleEdgeHover('bottom')}
              onMouseLeave={() => handleEdgeHover(null)}
              onContextMenu={(e) => handleContextMenu(e, 'bottom')}
              onMouseDown={(e) => handleEdgeResizeStart(e, 'bottom')}
            />
            <div
              aria-hidden='true'
              className={clsx(
                'absolute -left-1 bottom-2 top-2 w-2 cursor-w-resize transition-colors',
                activeEdge === 'left' && 'bg-blue-500',
                dropEdge === 'left' && 'bg-green-400 bg-opacity-70',
                hoveredEdge === 'left' &&
                  activeEdge !== 'left' &&
                  dropEdge !== 'left' &&
                  'bg-blue-300 bg-opacity-50'
              )}
              onMouseEnter={() => handleEdgeHover('left')}
              onMouseLeave={() => handleEdgeHover(null)}
              onContextMenu={(e) => handleContextMenu(e, 'left')}
              onMouseDown={(e) => handleEdgeResizeStart(e, 'left')}
            />
            {!easyMode && (
              <>
                <div
                  aria-hidden='true'
                  className='absolute -left-1 -top-1 z-[20] h-[8px] w-[8px] cursor-nw-resize bg-blue-600 opacity-0 group-hover:opacity-100'
                  onMouseDown={(e) => handleResizeStart(e, 'nw')}
                />
                <div
                  aria-hidden='true'
                  className='absolute -right-1 -top-1 z-[20] h-[8px] w-[8px] cursor-ne-resize bg-blue-600 opacity-0 group-hover:opacity-100'
                  onMouseDown={(e) => handleResizeStart(e, 'ne')}
                />
                <div
                  aria-hidden='true'
                  className='absolute -bottom-1 -left-1 z-[20] h-[8px] w-[8px] cursor-sw-resize bg-blue-600 opacity-0 group-hover:opacity-100'
                  onMouseDown={(e) => handleResizeStart(e, 'sw')}
                />
                <div
                  aria-hidden='true'
                  className='absolute -bottom-1 -right-1 z-[20] h-[8px] w-[8px] cursor-se-resize bg-blue-600 opacity-0 group-hover:opacity-100'
                  onMouseDown={(e) => handleResizeStart(e, 'se')}
                />
              </>
            )}
          </>
        )}
        {!useOpenToOutline && (
          <OpenFloorPlanWalls
            space={gridSpace}
            adjacentSpaces={getAllAdjacentSpaces(gridSpace, allGridSpaces)}
            structure={structure}
            hasSectionColor={!!hasSectionColor}
            overlay={overlay}
            hideOpenToWalls={
              !overlay && !isSelected && !activeSpace && !isPendingSelection
            }
            enlarged={activeSpace}
            conditioned={!!space?.conditioned}
          />
        )}
        {showInlineSpaceLabel && needsOverflowHover && (
          <SpaceInfoHoverCard
            open={isSpaceHovered}
            label={gridSpace.label}
            widthFt={gridSpace.position.width / 10}
            heightFt={gridSpace.position.height / 10}
            squareFootage={polygonSqft}
            BTUs={space?.BTUs}
            showBTUs={spaceLabelFit.showBtusInHover}
            showDimensions={spaceLabelFit.showDimensionsInHover}
            hasSectionColor={!!hasSectionColor}
            sectionTextColor={gridSpace.sectionColor?.text}
          />
        )}
      </div>
      {showInlineSpaceLabel && spaceLabelAnchor && (
        <div
          className={clsx(
            'pointer-events-none absolute overflow-visible',
            elevateSpaceLabel ? 'z-[21]' : 'z-[16]'
          )}
          style={polygonHandlesOverlayStyle}
        >
          <div
            className='absolute flex select-none flex-col items-center text-center text-sm font-medium'
            style={{
              left: spaceLabelAnchor.x,
              top: spaceLabelAnchor.y,
              transform: `translate(-50%, -50%) scale(${1 / effectiveZoom})`,
              transformOrigin: 'center',
              maxWidth: spaceLabelFit.screenMaxWidth,
              ...(hasSectionColor && gridSpace.sectionColor && !overlay
                ? { color: gridSpace.sectionColor.text }
                : {})
            }}
          >
            <div className='max-w-full break-normal leading-tight text-black'>
              {spaceLabelFit.displayLabel}
            </div>
            {spaceLabelFit.showDimensionsOnSpace &&
              spaceLabelDimensionsText && (
                <div
                  className={
                    hasSectionColor
                      ? 'whitespace-nowrap text-xs'
                      : 'whitespace-nowrap text-xs text-gray-500'
                  }
                  style={
                    hasSectionColor && gridSpace.sectionColor
                      ? {
                          color: gridSpace.sectionColor.text,
                          fontSize: '0.75rem',
                          opacity: 0.8
                        }
                      : undefined
                  }
                >
                  {spaceLabelDimensionsText}
                </div>
              )}
            {spaceLabelFit.showBtusOnSpace && space && (
              <BTUBubbles BTUs={space.BTUs} abbreviateNumbers />
            )}
            {showOverlayConditioningToggle && space && (
              <ConditioningToggle
                spaceId={gridSpace.spaceId}
                value={space.conditioned !== false}
                onChange={handleConditionedChange}
                capturePointer
              />
            )}
          </div>
        </div>
      )}
      {useOpenToOutline && (
        <GridSpaceOutlineSvg
          vertices={outlineVertices}
          bboxWidth={spaceBoxWidth}
          bboxHeight={spaceBoxHeight}
          stroke={getGridSpaceOutlineStroke({
            isHighlighted: activeSpace || isSelected || isPendingSelection,
            hasSectionColor: !!hasSectionColor,
            sectionBorder: gridSpace.sectionColor?.border,
            overlay
          })}
          strokeWidth={getGridSpaceOutlineStrokeWidth(
            activeSpace || isSelected || isPendingSelection
          )}
          hiddenIntervalsPerEdge={getHiddenIntervalsPerEdgeForGridSpace(
            gridSpace,
            allGridSpaces,
            space ?? null
          )}
          className='pointer-events-none absolute z-[15] overflow-visible'
          style={polygonHandlesOverlayStyle}
          segmentKeyPrefix='polygon-edge'
        />
      )}
      {!overlay && (
        <div
          aria-hidden='true'
          className={clsx(
            'pointer-events-none absolute',
            getGridSpaceOpeningsOverlayZClass({
              interactive: openingsInteractive,
              spaceElevated: isSpaceRootElevated
            })
          )}
          style={polygonHandlesOverlayStyle}
        >
          {(() => {
            const edgeItems = getEdgeItems();

            const handleEdgeItemContextMenu = (
              e: React.MouseEvent,
              item: EdgeItemType,
              run: EdgeRunType
            ) => {
              e.preventDefault();
              e.stopPropagation();

              const { edge } = run;

              const isWindow = item.type === 'window';
              const isGlassDoor = item.type === 'glass-door';
              const isInVerticalGlass = isWindow || isGlassDoor;
              let deleteLabel = 'Delete Door';
              if (isGlassDoor) deleteLabel = 'Delete Glass Door';
              else if (isWindow) deleteLabel = 'Delete Window';
              const menuItems: ContextMenuItemType[] = [
                {
                  label: 'Properties',
                  onClick: () => {
                    const structureSpace = findSpaceById(
                      structure,
                      gridSpace.spaceId
                    );

                    if (structureSpace) {
                      const activeItemData = createActiveItem({
                        type: 'space',
                        item: structureSpace
                      });

                      if (activeItemData) {
                        dispatch(
                          setActiveItem({
                            ...activeItemData,
                            showSizingOnly: true,
                            focusFenestrationId: item.id
                          })
                        );
                      }
                    }
                  }
                },
                {
                  separator: true
                },
                ...(isWindow
                  ? [
                      {
                        label: 'Change Size',
                        submenu: (
                          [
                            { label: 'Small', dragType: 'new-window-small' },
                            { label: 'Medium', dragType: 'new-window-medium' },
                            { label: 'Large', dragType: 'new-window-large' }
                          ] as const
                        ).map(({ label, dragType }) => ({
                          label,
                          onClick: () => {
                            const structureSpace = findSpaceById(
                              structure,
                              gridSpace.spaceId
                            );
                            if (!structureSpace?.sizingAttributes) return;
                            const size = OPENING_PRESETS[dragType];

                            dispatch(
                              updateStructureItem({
                                uniqueId: structureSpace.uniqueId,
                                sizingAttributes: updateWindowSize(
                                  structureSpace.sizingAttributes,
                                  edge,
                                  item.id,
                                  size.widthFt,
                                  size.heightFt,
                                  run,
                                  homeOrientation
                                )
                              })
                            );
                          }
                        }))
                      },
                      {
                        separator: true
                      }
                    ]
                  : []),
                {
                  label: deleteLabel,
                  onClick: () => {
                    const structureSpace = findSpaceById(
                      structure,
                      gridSpace.spaceId
                    );
                    if (!structureSpace?.sizingAttributes) return;

                    dispatch(
                      updateStructureItem({
                        uniqueId: structureSpace.uniqueId,
                        sizingAttributes: deleteEdgeItem(
                          structureSpace.sizingAttributes,
                          edge,
                          item.id,
                          isInVerticalGlass,
                          homeOrientation
                        )
                      })
                    );
                  }
                }
              ];

              openContextMenu(
                e.clientX,
                e.clientY,
                menuItems,
                `${gridSpace.id}-${item.type}-${item.id}`
              );
            };

            return (
              <>
                <SpaceEdgeOpenings
                  edgeItems={edgeItems}
                  edgeRuns={edgeRuns}
                  layouts={polygonOpeningLayouts}
                  spaceId={gridSpace.spaceId}
                  zoom={effectiveZoom}
                  interactive={openingsInteractive}
                  dragItemId={
                    openingsInteractive && isHomeOfDraggedOpening
                      ? activeDragItemId
                      : undefined
                  }
                  dragPlacement={
                    openingsInteractive ? openingDragPlacement : null
                  }
                  onContextMenu={
                    openingsInteractive ? handleEdgeItemContextMenu : undefined
                  }
                />
                {openingsInteractive && openingDragPlacement && dropPreview && (
                  <OpeningDropPreview
                    placement={openingDragPlacement}
                    itemType={dropPreview.itemType}
                    widthFt={dropPreview.widthFt}
                  />
                )}
              </>
            );
          })()}
          {(() => {
            const skylights = (
              space?.sizingAttributes?.ceilingsBelowRoof ?? []
            ).flatMap((c) => c.skylights ?? []);

            if (skylights.length === 0) return null;

            const handleSkylightContextMenu = (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();

              const skylightUniqueIds = skylights
                .map((s) => s.uniqueId)
                .filter(Boolean) as string[];

              const deleteLabel =
                skylights.length === 1 ? 'Delete Skylight' : 'Delete Skylights';

              const menuItems: ContextMenuItemType[] = [
                {
                  label: 'Properties',
                  onClick: () => {
                    const structureSpace = findSpaceById(
                      structure,
                      gridSpace.spaceId
                    );
                    if (structureSpace) {
                      const activeItemData = createActiveItem({
                        type: 'space',
                        item: structureSpace
                      });
                      if (activeItemData) {
                        dispatch(
                          setActiveItem({
                            ...activeItemData,
                            showSizingOnly: true
                          })
                        );
                      }
                    }
                  }
                },
                { separator: true },
                {
                  label: deleteLabel,
                  onClick: () => {
                    dispatch(
                      removeSkylightsFromSpace({
                        spaceId: gridSpace.spaceId,
                        skylightUniqueIds
                      })
                    );
                  }
                }
              ];

              openContextMenu(
                e.clientX,
                e.clientY,
                menuItems,
                `${gridSpace.id}-skylights`
              );
            };

            return (
              <SkylightBadge
                skylights={skylights}
                spaceId={gridSpace.spaceId}
                onContextMenu={handleSkylightContextMenu}
                zoom={zoom}
              />
            );
          })()}
        </div>
      )}
      {!overlay && !isStaticOrZones && !inMultiSpaceSection && !easyMode && (
        <div
          aria-hidden='true'
          className={clsx(
            'pointer-events-none absolute',
            isSelected
              ? GRID_SPACE_POLYGON_HANDLES_SELECTED_Z_CLASS
              : GRID_SPACE_POLYGON_HANDLES_Z_CLASS
          )}
          style={polygonHandlesOverlayStyle}
        >
          <PolygonHandles
            gridSpace={gridSpace}
            hoverState={polygonHandleDrag.hoverState}
            activeState={polygonHandleDrag.activeState}
            isSpaceHovered={isSpaceHovered}
            zoom={effectiveZoom}
            contextMenuEdgeIndex={contextMenuEdgeIndex}
            dropEdgeCardinal={dropEdge}
            dropEdgeIndex={openingDragPlacement?.run.edgeIndex ?? null}
            onVertexMouseDown={polygonHandleDrag.onVertexMouseDown}
            onEdgeMouseDown={polygonHandleDrag.onEdgeMouseDown}
            onVertexHoverChange={polygonHandleDrag.setHoverVertex}
            onEdgeHoverChange={polygonHandleDrag.setHoverEdge}
            onEdgeContextMenu={(e, edgeIndex, cardinal) =>
              handleEdgeContextMenu(e, edgeIndex, cardinal)
            }
            onVertexContextMenu={(e, vertexIndex) =>
              handleVertexContextMenu(e, vertexIndex)
            }
          />
        </div>
      )}
    </>
  );
}

export default memo(GridSpace);
